import { Box, Container, Typography, Grid, Card, Chip } from "@mui/material";
import React from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../contexts/AuthContext";
import config from "../config/env";

export const Dashboard: React.FC = React.memo(() => {
  const { user } = useAuth();
  const { t } = useTranslation('dashboard');

  // В development режиме React.StrictMode вызывает компоненты дважды
  // Это нормальное поведение для обнаружения побочных эффектов

  return (
    <Box>
      <Container maxWidth="xl" sx={{ py: 4 }}>
        {/* 🔐 Тестовые блоки подключаются и попадают в бандл только в development */}
        {config.IS_DEVELOPMENT && (() => {
          // В dev-режиме загружаем DevDashboardTests через require.
          // В production-сборке условие компилируется как false, и этот код вырезается бандлером.
          // eslint-disable-next-line @typescript-eslint/no-var-requires, global-require
          const DevDashboardTests = require("../components/test/DevDashboardTests").default as React.ComponentType;
          return <DevDashboardTests />;
        })()}
        
        <Typography variant="h4" component="h1" sx={{ fontWeight: 700, mb: 4 }}>
          {t('welcome')}, {user?.firstName || t('user')}!
        </Typography>

        <Typography variant="h6" sx={{ mb: 3, color: 'text.secondary' }}>
          {t('subtitle')}
        </Typography>

        {/* Информация о пользователе */}
        <Card sx={{ p: 3, mb: 4, bgcolor: 'primary.light', color: 'primary.contrastText' }}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            {t('profileInfo')}
          </Typography>
          <Typography variant="body1">
            {t('email')}: {user?.email}
          </Typography>
          <Typography variant="body1">
            {t('role')}: {user?.role}
          </Typography>
          <Typography variant="body1">
            ID: {user?.id}
          </Typography>
        </Card>

        {/* Быстрые ссылки */}
            <Typography variant="h5" component="h2" sx={{ mb: 3, fontWeight: 600 }}>
          🚀 {t('mainSections')}
            </Typography>

            <Grid container spacing={3}>
              {/* События */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                     <Typography variant="h6" sx={{ mb: 2, color: 'primary.main' }}>
                     📅 {t('sections.events')}
                   </Typography>
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                     <Link to="/events" style={{ textDecoration: 'none' }}>
                       <Chip label={t('actions.eventList')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                     <Link to="/events/create" style={{ textDecoration: 'none' }}>
                       <Chip label={t('actions.createEvent')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                 <Link to="/events/calendar" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.eventCalendar')} variant="outlined" />
                     </Link>
                   </Box>
                </Card>
              </Grid>

          {/* Маркетплейс */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                     <Typography variant="h6" sx={{ mb: 2, color: 'success.main' }}>
                 🛒 {t('sections.marketplace')}
                   </Typography>
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                 <Link to="/marketplace/search" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.searchContractors')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                 <Link to="/marketplace/favorites" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.favoriteSuppliers')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                 <Link to="/marketplace/compare" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.compareOffers')} variant="outlined" />
                     </Link>
                   </Box>
                </Card>
              </Grid>

          {/* Задачи */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                     <Typography variant="h6" sx={{ mb: 2, color: 'warning.main' }}>
                 ✅ {t('sections.tasks')}
                   </Typography>
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                 <Link to="/tasks" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.taskList')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                 <Link to="/tasks/create" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.createTask')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                 <Link to="/tasks" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.taskDashboard')} variant="outlined" />
                     </Link>
                   </Box>
                </Card>
              </Grid>

          {/* Пользователи */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                     <Typography variant="h6" sx={{ mb: 2, color: 'info.main' }}>
                 👥 {t('sections.participants')}
                   </Typography>
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                 <Link to="/users" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.participantList')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                 <Link to="/users/create" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.addParticipant')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                 <Link to="/settings/profile" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.myProfile')} variant="outlined" />
                     </Link>
                   </Box>
                </Card>
              </Grid>

              {/* Аналитика */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                     <Typography variant="h6" sx={{ mb: 2, color: 'error.main' }}>
                     📊 {t('sections.analytics')}
                   </Typography>
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                 <Link to="/analytics" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.analyticsDashboard')} variant="outlined" sx={{ mb: 1 }} />
                 </Link>
                     <Link to="/analytics/builder" style={{ textDecoration: 'none' }}>
                       <Chip label={t('actions.dashboardBuilder')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                 <Link to="/supplier-analytics" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.supplierAnalytics')} variant="outlined" />
                     </Link>
                   </Box>
                </Card>
              </Grid>

          {/* Настройки */}
              <Grid item xs={12} sm={6} md={4}>
                <Card sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                                     <Typography variant="h6" sx={{ mb: 2, color: 'secondary.main' }}>
                     ⚙️ {t('sections.settings')}
                   </Typography>
                   <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                     <Link to="/settings" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.generalSettings')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                 <Link to="/settings/notifications" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.notifications')} variant="outlined" sx={{ mb: 1 }} />
                     </Link>
                 <Link to="/personalization" style={{ textDecoration: 'none' }}>
                   <Chip label={t('actions.personalization')} variant="outlined" />
                     </Link>
                   </Box>
                </Card>
              </Grid>
            </Grid>

        {/* Статус системы */}
                 <Box sx={{ mt: 4, p: 2, bgcolor: 'success.light', borderRadius: 1 }}>
           <Typography variant="body2" sx={{ color: 'success.dark' }}>
             ✅ {t('systemStatus')}
           </Typography>
         </Box>
      </Container>
    </Box>
  );
});

export default Dashboard;